import {
  cloneElement,
  createElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import gsap from 'gsap';

/**
 * StaggerHeading — GSAP-driven letter cascade for h1/h2/h3.
 *
 * The component walks `children`, splits string nodes into per-character
 * <span> elements, preserves any inline element nodes (e.g. accent
 * <span className="t-italic-accent">), and runs a single GSAP timeline
 * triggered when the heading scrolls into view.
 *
 * Same easing as the Sign-off wordmark stagger so the page reads as one
 * motion language.
 *
 * Reduced-motion: skips the animation, renders text static.
 */
type Props = {
  /** Tag to render. h1 / h2 / h3 typically. */
  as?: 'h1' | 'h2' | 'h3' | 'p';
  /** Stagger delay between letters (s). Default 0.04. */
  stagger?: number;
  /** Each-letter rise duration (s). Default 0.9. */
  duration?: number;
  /** Whether to fire only once. Default true. */
  once?: boolean;
  /** Optional className passthrough. */
  className?: string;
  /** Inline style passthrough. */
  style?: CSSProperties;
  children: ReactNode;
};

export function StaggerHeading({
  as = 'h2',
  stagger = 0.04,
  duration = 0.9,
  once = true,
  className,
  style,
  children,
}: Props) {
  const rootRef = useRef<HTMLElement>(null);

  // Walk children, splitting string nodes into per-letter spans.
  // Inline elements (like the italic accent <span>) keep their structure
  // and have THEIR text split too — so the whole heading staggers.
  const split = useMemo(() => splitNodes(children), [children]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const letters = root.querySelectorAll<HTMLElement>('[data-stagger-char]');
    if (letters.length === 0) return;

    if (reduced) {
      // Reset to visible static state so the heading is readable even
      // if a previous run set yPercent/opacity.
      gsap.set(letters, { yPercent: 0, opacity: 1 });
      return;
    }

    let played = false;
    const play = () => {
      if (played) return;
      played = true;
      gsap.to(letters, {
        yPercent: 0,
        opacity: 1,
        duration,
        ease: 'power3.out',
        stagger,
      });
    };

    // Start the letters below the line; if the heading is already in
    // viewport at mount (hero h1) we play immediately, otherwise an
    // IntersectionObserver waits until ~15% of the heading is on screen.
    // This sidesteps any race with Lenis / ScrollTrigger init order that
    // previously left above-the-fold headings stuck invisible.
    gsap.set(letters, { yPercent: 110, opacity: 0 });

    const rect = root.getBoundingClientRect();
    const inViewNow = rect.top < window.innerHeight * 0.95 && rect.bottom > 0;
    if (inViewNow) {
      play();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            play();
            if (once) io.disconnect();
          }
        }
      },
      { threshold: 0.15 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, [duration, stagger, once, split]);

  // createElement avoids the JSX-namespace TS issue in React 19 strict mode.
  return createElement(
    as,
    { ref: rootRef, className, style },
    split,
  );
}

/* ── helpers ───────────────────────────────────────────────────────── */

let key = 0;

function splitNodes(nodes: ReactNode): ReactNode {
  const out: ReactNode[] = [];

  // Normalize to an array we can walk regardless of single-vs-array shape.
  const arr = Array.isArray(nodes) ? nodes : [nodes];
  for (const node of arr) {
    if (typeof node === 'string') {
      out.push(...splitString(node));
    } else if (typeof node === 'number') {
      out.push(...splitString(String(node)));
    } else if (isValidElement(node)) {
      const el = node as ReactElement<{ children?: ReactNode }>;
      const innerChildren = el.props?.children;
      out.push(
        cloneElement(el, { key: `el-${key++}` }, splitNodes(innerChildren)),
      );
    } else {
      out.push(node);
    }
  }
  return out;
}

function splitString(s: string): ReactNode[] {
  // Each character becomes a span. Spaces use a non-breaking space inside
  // an inline-block so the rise animation visually preserves word breaks.
  // The outer wrapper has overflow:hidden so the rise comes from below.
  const result: ReactNode[] = [];
  for (const ch of s) {
    const isSpace = ch === ' ';
    result.push(
      <span
        key={`c-${key++}`}
        style={{
          display: 'inline-block',
          overflow: 'hidden',
          verticalAlign: 'top',
          whiteSpace: 'pre',
        }}
      >
        <span
          data-stagger-char
          style={{
            display: 'inline-block',
            willChange: 'transform, opacity',
          }}
        >
          {isSpace ? ' ' : ch}
        </span>
      </span>,
    );
  }
  return result;
}
